import {get, post, patch, del} from './http';

export type ScrapyCollection = {
  id: string;
  collection_group_id: string;
  name: string;
  description: string | null;
  spider_name: string;
  spider_args: Record<string, string>;
  cron_expression: string | null;
  is_active: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
  affiliate: string | null;
  policy_violation: string | null;
  language: string | null;
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
};

export type ScrapyCollectionRun = {
  id: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'timed_out';
  item_count: number;
  created_at: string;
  completed_at: string | null;
  is_test_run: boolean;
};

/**
 One (UTC day x collection-group x label-tuple) bucket of collected items. Every dimension the
 overview chart can group by rides on the row, so switching group-by, filtering or drilling down
 is a local regroup rather than a refetch. Labels are the run-time snapshot, so a renamed or
 re-queried collection keeps its history under the labels it was collected under -
 `collection_group_id` is the stable identity across edits, `name` is not.
 */
export type ScrapyCollectionDailyStat = {
  date: string;
  collection_group_id: string;
  name: string;
  affiliate: string | null;
  policy_violation: string | null;
  language: string | null;
  query: string;
  item_count: number;
};

export type TestRunResult = {
  job_id: string;
  status: string;
};

export type TestRunWindow = {
  window_size: number;
  window_unit: 'min' | 'hour' | 'day';
};

export type FieldChange = {
  old: string | null;
  new: string | null;
};

export type ScrapyCollectionHistoryEntry = {
  id: string;
  audit_type: 'created' | 'deleted';
  changed_by: string;
  changed_at: string;
  changes: Record<string, FieldChange>;
};

export type CreateScrapyCollectionRequest = {
  name: string;
  description?: string | null;
  spider_name?: string;
  spider_args: Record<string, string>;
  cron_expression?: string | null;
  is_active?: boolean;
  affiliate?: string | null;
  policy_violation?: string | null;
  language?: string | null;
};

export type PatchScrapyCollectionRequest = {
  name?: string;
  description?: string | null;
  spider_args?: Record<string, string>;
  cron_expression?: string | null;
  clear_cron_expression?: boolean;
  is_active?: boolean;
  affiliate?: string | null;
  policy_violation?: string | null;
  language?: string | null;
  clear_affiliate?: boolean;
  clear_policy_violation?: boolean;
  clear_language?: boolean;
};

export const scrapyCollectionsApi = {
  list: async () => get<ScrapyCollection[]>('/admin/scrapy-collections'),
  get: async (id: string) => get<ScrapyCollection>(`/admin/scrapy-collections/${id}`),
  create: async (body: CreateScrapyCollectionRequest) =>
    post<CreateScrapyCollectionRequest, ScrapyCollection>('/admin/scrapy-collections', body),
  update: async (id: string, body: PatchScrapyCollectionRequest) =>
    patch<PatchScrapyCollectionRequest, ScrapyCollection>(`/admin/scrapy-collections/${id}`, body),
  delete: async (id: string) => del(`/admin/scrapy-collections/${id}`),
  run: async (id: string) =>
    post<Record<string, never>, ScrapyCollectionRun>(`/admin/scrapy-collections/${id}/run`, {}),
  runs: async (id: string, opts?: {includeTestRuns?: boolean}) =>
    get<ScrapyCollectionRun[]>(`/admin/scrapy-collections/${id}/runs`, {
      include_test_runs: opts?.includeTestRuns ?? false
    }),
  dailyStats: async (opts?: {days?: number; includeTestRuns?: boolean}) =>
    get<ScrapyCollectionDailyStat[]>('/admin/scrapy-collections/daily-stats', {
      days: opts?.days ?? 90,
      include_test_runs: opts?.includeTestRuns ?? false
    }),
  testRun: async (id: string, window?: TestRunWindow) =>
    post<TestRunWindow | Record<string, never>, TestRunResult>(
      `/admin/scrapy-collections/${id}/test-run`,
      window ?? {}
    ),
  getJob: async (id: string, jobId: string) =>
    get<ScrapyCollectionRun>(`/admin/scrapy-collections/${id}/jobs/${jobId}`),
  history: async (id: string) =>
    get<ScrapyCollectionHistoryEntry[]>(`/admin/scrapy-collections/${id}/history`)
};
